/** Registration details of an agency (PUL-3224), the backend `AgencyDetailsDTO`. */
export interface AgencyDetails {
  bookingPhone?: string | null;
  bookingEmail?: string | null;
  networkMember?: boolean | null;
  networkName?: string | null;
  companyRegistrationNumber?: string | null;
  atoutFranceNumber?: string | null;
  financialGuarantee?: string | null;
  financeManagerLastName?: string | null;
  financeManagerFirstName?: string | null;
  financeManagerPhone?: string | null;
  financeManagerEmail?: string | null;
}

/** Files an agency provides; the values are the backend path segments. */
export type AgencyDocumentSlug =
  | 'logo'
  | 'registration-certificate'
  | 'financial-guarantee';

export interface AgencyDocument {
  /** Static public URL of a logo, null for a private document. */
  url?: string | null;
  originalFilename?: string | null;
  contentType?: string | null;
  size?: number | null;
  uploadedAt?: string | null;
}

export const AGENCY_DOCUMENT_LABELS: Record<AgencyDocumentSlug, string> = {
  logo: 'Logo (carnets de voyage)',
  'registration-certificate': "Certificat d'immatriculation",
  'financial-guarantee': 'Attestation de garantie financière',
};

/**
 * Formats the backend accepts for each document (`AgencyDocumentType`). The
 * logo is stored as an optimized PNG whatever its source format.
 */
export const AGENCY_DOCUMENT_ACCEPT: Record<AgencyDocumentSlug, string[]> = {
  logo: ['image/png', 'image/jpeg', 'image/webp'],
  'registration-certificate': ['application/pdf', 'image/png', 'image/jpeg'],
  'financial-guarantee': ['application/pdf', 'image/png', 'image/jpeg'],
};

/** Backend ceiling per file (`AgencyDocumentService.MAX_FILE_SIZE`). */
export const AGENCY_DOCUMENT_MAX_SIZE = 10 * 1024 * 1024;

/** French label of each field the backend may report in `missingFields`. */
export const AGENCY_FIELD_LABELS: Record<string, string> = {
  name: "Nom de l'agence",
  address: 'Adresse',
  zipcode: 'Code postal',
  country: 'Pays',
  city: 'Ville',
  phone: 'Téléphone',
  email: 'Email',
  bookingPhone: 'Téléphone service réservation',
  bookingEmail: 'Email service réservation',
  networkMember: 'Appartenance à un réseau',
  networkName: 'Nom du réseau',
  logo: AGENCY_DOCUMENT_LABELS.logo,
  companyRegistrationNumber: 'Immatriculation société',
  atoutFranceNumber: 'Immatriculation Atout France',
  registrationCertificate: AGENCY_DOCUMENT_LABELS['registration-certificate'],
  financialGuarantee: 'Garantie financière',
  financialGuaranteeCertificate: AGENCY_DOCUMENT_LABELS['financial-guarantee'],
  financeManagerLastName: 'Nom du responsable financier',
  financeManagerFirstName: 'Prénom du responsable financier',
  financeManagerPhone: 'Téléphone du responsable financier',
  financeManagerEmail: 'Email du responsable financier',
};

/** ISO 3166-1 alpha-2 codes, the format the user service stores in `country` (PUL-3298). */
export const AGENCY_COUNTRY_CODES: readonly string[] = [
  'AD', 'AE', 'AF', 'AG', 'AI', 'AL', 'AM', 'AO', 'AQ', 'AR', 'AS',
  'AT', 'AU', 'AW', 'AX', 'AZ', 'BA', 'BB', 'BD', 'BE', 'BF', 'BG', 'BH',
  'BI', 'BJ', 'BL', 'BM', 'BN', 'BO', 'BQ', 'BR', 'BS', 'BT', 'BV', 'BW',
  'BY', 'BZ', 'CA', 'CC', 'CD', 'CF', 'CG', 'CH', 'CI', 'CK', 'CL', 'CM',
  'CN', 'CO', 'CR', 'CU', 'CV', 'CW', 'CX', 'CY', 'CZ', 'DE', 'DJ', 'DK',
  'DM', 'DO', 'DZ', 'EC', 'EE', 'EG', 'EH', 'ER', 'ES', 'ET', 'FI', 'FJ',
  'FK', 'FM', 'FO', 'FR', 'GA', 'GB', 'GD', 'GE', 'GF', 'GG', 'GH', 'GI',
  'GL', 'GM', 'GN', 'GP', 'GQ', 'GR', 'GS', 'GT', 'GU', 'GW', 'GY', 'HK',
  'HM', 'HN', 'HR', 'HT', 'HU', 'ID', 'IE', 'IL', 'IM', 'IN', 'IO', 'IQ',
  'IR', 'IS', 'IT', 'JE', 'JM', 'JO', 'JP', 'KE', 'KG', 'KH', 'KI', 'KM',
  'KN', 'KP', 'KR', 'KW', 'KY', 'KZ', 'LA', 'LB', 'LC', 'LI', 'LK', 'LR',
  'LS', 'LT', 'LU', 'LV', 'LY', 'MA', 'MC', 'MD', 'ME', 'MF', 'MG', 'MH',
  'MK', 'ML', 'MM', 'MN', 'MO', 'MP', 'MQ', 'MR', 'MS', 'MT', 'MU', 'MV',
  'MW', 'MX', 'MY', 'MZ', 'NA', 'NC', 'NE', 'NF', 'NG', 'NI', 'NL', 'NO',
  'NP', 'NR', 'NU', 'NZ', 'OM', 'PA', 'PE', 'PF', 'PG', 'PH', 'PK', 'PL',
  'PM', 'PN', 'PR', 'PS', 'PT', 'PW', 'PY', 'QA', 'RE', 'RO', 'RS', 'RU',
  'RW', 'SA', 'SB', 'SC', 'SD', 'SE', 'SG', 'SH', 'SI', 'SJ', 'SK', 'SL',
  'SM', 'SN', 'SO', 'SR', 'SS', 'ST', 'SV', 'SX', 'SY', 'SZ', 'TC', 'TD',
  'TF', 'TG', 'TH', 'TJ', 'TK', 'TL', 'TM', 'TN', 'TO', 'TR', 'TT', 'TV',
  'TW', 'TZ', 'UA', 'UG', 'UM', 'US', 'UY', 'UZ', 'VA', 'VC', 'VE', 'VG',
  'VI', 'VN', 'VU', 'WF', 'WS', 'YE', 'YT', 'ZA', 'ZM', 'ZW',
];

const COUNTRY_NAMES = new Intl.DisplayNames(['fr'], { type: 'region' });

/** French name of a country code, the code itself when unknown. */
export function countryName(code?: string | null): string {
  if (!code) return '';
  try {
    return COUNTRY_NAMES.of(code) ?? code;
  } catch {
    return code;
  }
}

/** Every country, named in French and sorted by name, for a select. */
export const AGENCY_COUNTRY_OPTIONS: { code: string; name: string }[] =
  AGENCY_COUNTRY_CODES.map((code) => ({ code, name: countryName(code) })).sort(
    (a, b) => a.name.localeCompare(b.name, 'fr')
  );

export function describeMissingFields(missingFields?: string[] | null): string {
  return (missingFields ?? [])
    .map((field) => AGENCY_FIELD_LABELS[field] ?? field)
    .join(', ');
}

export interface IAgency {
  id?: string;
  agencyCode?: string;
  nameAgency?: string;
  nameAgent?: string;
  surnameAgent?: string;
  address?: string;
  postalCode?: string;
  city?: string;
  /** ISO 3166-1 alpha-2 code (PUL-3298); absent on agencies predating it. */
  country?: string | null;
  phone?: string;
  mail?: string;
  supplierId?: string;
  canBook?: boolean;
  bookingDeniedMessage?: string | null;
  // Registration details (PUL-3224). `details` left out of a write keeps the stored values.
  details?: AgencyDetails | null;
  /** Read-only: files only change through the document endpoints. */
  documents?: Partial<Record<AgencyDocumentSlug, AgencyDocument>> | null;
  /** Read-only: follows the logo through the document endpoints. */
  logoUrl?: string | null;
  complete?: boolean | null;
  missingFields?: string[] | null;
  completionRequestedAt?: string | null;
}

export function normalizeBookingDeniedMessage(value?: string | null): string {
  return value?.trim() ?? '';
}

export class Agency implements IAgency {
  address!: string;
  city!: string;
  agencyCode!: string;
  id!: string;
  mail!: string;
  nameAgency!: string;
  nameAgent!: string;
  phone!: string;
  postalCode!: string;
  country?: string | null;
  surnameAgent!: string;
  supplierId!: string;
  canBook?: boolean;
  bookingDeniedMessage?: string | null;
}
